import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/safeToast";
import { FileText, Upload, ExternalLink, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";

const BUCKET = "clinical-documents";
const MAX_BYTES = 25 * 1024 * 1024;

export function ClinicalDocumentVault({ patientId }: { patientId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const folder = user?.id ? `${user.id}/${patientId}` : "";

  const { data: files = [], isLoading, error } = useQuery({
    queryKey: ["clinical-documents", user?.id, patientId],
    enabled: !!user?.id && !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(BUCKET).list(folder, {
        limit: 100,
        sortBy: { column: "created_at", order: "desc" },
      });
      if (error) throw error;
      return (data ?? []).filter((file: any) => file.name && !file.name.startsWith("."));
    },
  });

  async function upload(file?: File) {
    if (!file || !user?.id) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Envie somente arquivos PDF.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("O PDF ultrapassa o limite de 25 MB.");
      return;
    }
    setUploading(true);
    const safeName = file.name.normalize("NFKD").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(-120);
    const objectPath = `${folder}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from(BUCKET).upload(objectPath, file, {
      contentType: "application/pdf",
      upsert: false,
      cacheControl: "3600",
    });
    setUploading(false);
    if (error) {
      toast.error(`Falha ao enviar PDF: ${error.message}. Confirme se a migration do cofre clínico foi aplicada.`);
      return;
    }
    toast.success("PDF armazenado no cofre privado do paciente.");
    await qc.invalidateQueries({ queryKey: ["clinical-documents", user.id, patientId] });
  }

  async function openFile(name: string) {
    if (!user?.id || !folder) return;
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(`${folder}/${name}`, 60);
    if (error || !data?.signedUrl) {
      toast.error(error?.message ?? "Não foi possível gerar o link temporário.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  async function deleteFile(name: string) {
    if (!user?.id || !folder || deleting) return;
    const confirmed = window.confirm("Excluir este PDF permanentemente? Esta ação não pode ser desfeita.");
    if (!confirmed) return;
    setDeleting(name);
    const { error } = await supabase.storage.from(BUCKET).remove([`${folder}/${name}`]);
    setDeleting(null);
    if (error) {
      toast.error(`Não foi possível excluir o PDF: ${error.message}`);
      return;
    }
    toast.success("PDF excluído.");
    await qc.invalidateQueries({ queryKey: ["clinical-documents", user.id, patientId] });
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" />Documentos e avaliações em PDF</h3>
          <p className="text-sm text-muted-foreground mt-1">Laudos, avaliações neuropsiquiátricas, escalas e documentos clínicos. Arquivos privados, separados por paciente.</p>
        </div>
        <Badge variant="outline"><ShieldCheck className="h-3 w-3 mr-1" />Privado</Badge>
      </div>
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50">
        <Upload className="h-4 w-4" />
        {uploading ? "Enviando…" : "Enviar PDF (máx. 25 MB)"}
        <input className="sr-only" type="file" accept="application/pdf,.pdf" disabled={uploading} onChange={(e) => { const file = e.target.files?.[0]; void upload(file); e.currentTarget.value = ""; }} />
      </label>
      {isLoading ? <p className="text-sm text-muted-foreground">Carregando documentos…</p> : null}
      {error ? <div role="alert" className="text-sm text-destructive">Não foi possível listar PDFs: {(error as Error).message}. A migration do cofre precisa estar aplicada no banco.</div> : null}
      {!isLoading && !error && files.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum PDF anexado ainda.</p> : null}
      {files.map((file: any) => (
        <div key={file.name} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
          <div className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-muted-foreground" /><div className="min-w-0"><div className="truncate text-sm font-medium">{file.name.replace(/^\d+-/, "")}</div><div className="text-xs text-muted-foreground">{file.created_at ? new Date(file.created_at).toLocaleString("pt-BR") : "Data não informada"}{file.metadata?.size ? ` • ${(file.metadata.size / 1024 / 1024).toFixed(1)} MB` : ""}</div></div></div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void openFile(file.name)}>Abrir PDF <ExternalLink className="h-3.5 w-3.5 ml-1" /></Button>
            <Button size="sm" variant="destructive" disabled={deleting !== null} onClick={() => void deleteFile(file.name)} aria-label={`Excluir ${file.name}`}>
              {deleting === file.name ? "Excluindo…" : <><Trash2 className="h-3.5 w-3.5 mr-1" />Excluir</>}
            </Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => qc.invalidateQueries({ queryKey: ["clinical-documents", user?.id, patientId] })}><RefreshCw className="h-3.5 w-3.5 mr-1" />Atualizar lista</Button>
      <p className="text-xs text-muted-foreground">O sistema pode usar o conteúdo do PDF para preparar um resumo clínico e sinalizar escalas, achados e diferenciais, mas a extração e a interpretação precisam ser revisadas pelo médico antes de incorporar ao prontuário.</p>
    </Card>
  );
}
